<template>

  <report-panel prepend-icon="$revenue" :title="$t('titles.revenue_by_weekday')" :loading="loading">

    <!-- <template v-slot:tools> -->

      <v-row>
        <v-col class="text-end mb-5">
          <v-btn-toggle
            v-model="formState.mode"
            rounded="0"
            group
            divided
            variant="outlined"
            mandatory
            density="compact"
          >
            <v-btn v-for="modeOption in modeOptions" :value="modeOption.value">
              <span class="d-sm-none">{{ modeOption.mobileTitle }}</span>
              <span class="d-none d-sm-inline">{{ modeOption.title }}</span>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

    <!-- </template> -->

    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, reactive, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale, type ChartData, type ChartOptions, type TooltipItem,
} from 'chart.js'
import type { ReportingDateSummaryDollarAmountBreakdown } from '../../../../services/api-clients/ReportingApi'
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale
)

const props = defineProps({
  
  loading: {
    type: Boolean,
    default: false,
  },

  dateSummaries: {
    type: Object,
    required: true,
  },

  channels: {
    type: Array,
    default: [],
  },

})

const { n, t } = useI18n()

interface FormState {
  mode: 'transaction_date' | 'start_date'
}

const formState = reactive<FormState>({
  mode: 'transaction_date',
})

const modeOptions = [
  {value: 'start_date', title: t('titles.start_date'), mobileTitle: t('titles.start_date_short')},
  {value: 'transaction_date', title: t('titles.transaction_date'), mobileTitle: t('titles.transaction_date_short')},
]

/**
 * Chart
 * https://www.chartjs.org/docs/latest/samples/other-charts/stacked-bar-line.html
 */

const chartOptions: ChartOptions<'bar'> = {
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          return n(context.raw as number, 'currency')
        },
      },
    },
  },
  scales: {
    y: {
      ticks: {
        callback: (value, _index, _values) => {
          return n(value as number, {style: 'currency', currency: 'USD', maximumFractionDigits: 0})
        },
      },
    },
  },
}

const chartData = computed<ChartData<'bar', number[]>>(() => {

  const totals = [
    {revenue: 0, days: 0}, {revenue: 0, days: 0}, {revenue: 0, days: 0},
    {revenue: 0, days: 0}, {revenue: 0, days: 0}, {revenue: 0, days: 0},
    {revenue: 0, days: 0},
  ]

  Object.entries(props.dateSummaries).forEach(([dateString, summary]) => {

    // ES6 says that Sunday = 0. We say that Monday = 0.
    const es6Weekday = new Date(dateString).getUTCDay()
    const ourWeekday = es6Weekday === 0 ? 6 : es6Weekday - 1

    totals[ourWeekday].days++

    let gross_revenue: ReportingDateSummaryDollarAmountBreakdown | null = null

    switch (formState.mode) {
      case 'transaction_date':
        gross_revenue = summary.as_transaction_date.gross_revenue
        break
      case 'start_date':
        gross_revenue = summary.as_start_date.gross_revenue
        break
    }

    Object.entries(gross_revenue!.by_channel).forEach(([channel, grossRevenue]) => {
      if (!props.channels.length || props.channels.includes(channel)) {
        totals[ourWeekday].revenue += grossRevenue
      }
    })
    
  })

  const data = totals.map((item) => {
    return item.days > 0 ? item.revenue / item.days : 0
  })

  return {
    labels: [
      t('enums.weekdays_abbr.mon'),
      t('enums.weekdays_abbr.tue'),
      t('enums.weekdays_abbr.wed'),
      t('enums.weekdays_abbr.thu'),
      t('enums.weekdays_abbr.fri'),
      t('enums.weekdays_abbr.sat'),
      t('enums.weekdays_abbr.sun'),
    ],
    datasets: [
      {
        type: 'bar',
        backgroundColor: '#78a8c8',
        data: data,
      },
    ],
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const container = useTemplateRef('chartContainer')
const { chartStyles } = useFitChartToContainer(container)

</script>
